/**
 * Allowlisted mirror of selected PostHog events to GA4 (gtag.js).
 * PostHog remains the full-fidelity source; GA4 receives a small funnel subset only.
 * Does not send page_view (gtag config already does) or section_viewed (avoid clash with Enhanced Measurement scroll).
 * Requires `window.gtag` from `registerThirdPartyScripts` (stub is installed before gtag.js loads).
 */

import {
  ANALYTICS_FLAGS,
  ANALYTICS_STORAGE_KEYS,
  DEFAULT_EVENT_PROPERTIES,
} from "../../constants/analytics";
import { AnalyticsEvents } from "../../types/analytics";

const FIGMA_PLUGIN_ITEM_ID = "1564367214330636618";

type GtagFn = (...args: unknown[]) => void;

function getGtag(): GtagFn | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  const w = window as unknown as { gtag?: GtagFn };
  return typeof w.gtag === "function" ? w.gtag : undefined;
}

/** Aligns with PostHog client opt-outs (not skipInTests: unit tests may assert GA mapping with a mocked gtag). */
export function isGa4MirrorAllowed(): boolean {
  if (!ANALYTICS_FLAGS.enabled) {
    return false;
  }
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      if (
        window.localStorage.getItem(ANALYTICS_STORAGE_KEYS.TRACKING_ENABLED) ===
        "false"
      ) {
        return false;
      }
      if (
        window.localStorage.getItem(ANALYTICS_STORAGE_KEYS.INTERNAL_USER) ===
        "true"
      ) {
        return false;
      }
    }
  } catch {
    return false;
  }
  return true;
}

let userPropertiesSent = false;

/** Clears one-shot GA user-property flag; for unit tests only. */
export function resetGa4MirrorStateForTests(): void {
  userPropertiesSent = false;
}

function sendUserPropertiesOnce(gtag: GtagFn): void {
  if (userPropertiesSent) {
    return;
  }
  userPropertiesSent = true;
  try {
    gtag(
      "set",
      "user_properties",
      compactGtagParams({
        launch_phase: String(DEFAULT_EVENT_PROPERTIES.launch_phase),
      }),
    );
  } catch {
    userPropertiesSent = false;
  }
}

function hostnameOnly(url: string): string | undefined {
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
}

function pickString(
  props: Record<string, unknown>,
  key: string,
): string | undefined {
  const v = props[key];
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function pickBoolean(
  props: Record<string, unknown>,
  key: string,
): boolean | undefined {
  const v = props[key];
  return typeof v === "boolean" ? v : undefined;
}

/** Drop undefined so GA4 receives smaller payloads and fewer custom-dimension gaps. */
function compactGtagParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined),
  );
}

/**
 * Maps allowlisted analytics event names to GA4 gtag calls.
 * Unknown or disallowed events are ignored.
 */
export function mirrorEventToGa4(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  if (!isGa4MirrorAllowed()) {
    return;
  }

  const gtag = getGtag();
  if (!gtag) {
    return;
  }

  sendUserPropertiesOnce(gtag);

  switch (eventName) {
    case AnalyticsEvents.PLUGIN_INSTALL_CLICKED: {
      const destinationUrl = pickString(properties, "destination_url");
      gtag(
        "event",
        "select_content",
        compactGtagParams({
          content_type: "figma_plugin",
          item_id: FIGMA_PLUGIN_ITEM_ID,
          cta_location: pickString(properties, "cta_location"),
          destination_host: destinationUrl
            ? hostnameOnly(destinationUrl)
            : undefined,
          transport_type: "beacon",
        }),
      );
      break;
    }
    case AnalyticsEvents.INSTALL_INTENT_CREATED: {
      gtag(
        "event",
        "install_intent_created",
        compactGtagParams({
          install_intent_id: pickString(properties, "install_intent_id"),
          fingerprint: pickString(properties, "fingerprint"),
          cta_location: pickString(properties, "cta_location"),
          page_path: pickString(properties, "page_path"),
          page_location: pickString(properties, "page_location"),
          referrer:
            pickString(properties, "referrer") ||
            (typeof document !== "undefined" && document.referrer
              ? document.referrer
              : undefined),
          store_success: pickBoolean(properties, "store_success"),
          anonymous_id: pickString(properties, "anonymous_id"),
          ref: pickString(properties, "ref"),
          utm_source: pickString(properties, "utm_source"),
          utm_medium: pickString(properties, "utm_medium"),
          utm_campaign: pickString(properties, "utm_campaign"),
          utm_term: pickString(properties, "utm_term"),
          utm_content: pickString(properties, "utm_content"),
          transport_type: "beacon",
        }),
      );
      break;
    }
    case AnalyticsEvents.DEMO_INTERACTED: {
      gtag(
        "event",
        "demo_interacted",
        compactGtagParams({
          interaction_type: pickString(properties, "interaction_type"),
          demo_location: pickString(properties, "demo_location"),
        }),
      );
      break;
    }
    case AnalyticsEvents.EMAIL_CAPTURED: {
      gtag(
        "event",
        "generate_lead",
        compactGtagParams({
          currency: "USD",
          value: 0,
          lead_source: pickString(properties, "source"),
          email_domain: pickString(properties, "email_domain"),
        }),
      );
      break;
    }
    case AnalyticsEvents.PRICING_VIEWED: {
      gtag(
        "event",
        "view_promotion",
        compactGtagParams({
          promotion_name: "pro_tier",
          creative_name: pickString(properties, "plan_viewed") || "pricing_section",
          creative_slot: "pricing_table",
          location_id: "landing_page",
        }),
      );
      break;
    }
    case AnalyticsEvents.VIDEO_PLAYED: {
      gtag(
        "event",
        "video_start",
        compactGtagParams({
          video_title: pickString(properties, "video_id"),
          video_provider: "self_hosted",
          video_url: pickString(properties, "video_location"),
        }),
      );
      break;
    }
    case AnalyticsEvents.CTA_CLICKED: {
      gtag(
        "event",
        "select_content",
        compactGtagParams({
          content_type: "button",
          item_id: pickString(properties, "cta_text"),
          cta_location: pickString(properties, "cta_location"),
        }),
      );
      break;
    }
    default:
      break;
  }
}
